/**
 * Parent PWA → admin club feedback (sessions, suggestions, general queries).
 */

export const FEEDBACK_CATEGORIES = [
  {
    code: 'app_help',
    label: 'App help (sign-in or bugs)',
    shortLabel: 'App help',
    description: 'Cannot sign in, missing sessions, or something in the app looks wrong',
  },
  {
    code: 'swimming_sessions',
    label: 'Swimming sessions',
    shortLabel: 'Sessions',
    description: 'Training quality, coaches, pool, attendance issues',
  },
  {
    code: 'club_suggestions',
    label: 'Club suggestions',
    shortLabel: 'Suggestions',
    description: 'Ideas to improve the club, events, or app',
  },
  {
    code: 'general_queries',
    label: 'General queries',
    shortLabel: 'Queries',
    description: 'Fees, induction, meets, or anything else',
  },
] as const;

export type FeedbackCategory = (typeof FEEDBACK_CATEGORIES)[number]['code'];

export const FEEDBACK_CATEGORY_CODES = FEEDBACK_CATEGORIES.map((c) => c.code);

export function isFeedbackCategory(value: string): value is FeedbackCategory {
  return (FEEDBACK_CATEGORY_CODES as readonly string[]).includes(value);
}

export function feedbackCategoryLabel(code: string): string {
  return FEEDBACK_CATEGORIES.find((c) => c.code === code)?.label ?? code;
}

export const FEEDBACK_STATUSES = ['new', 'reviewed', 'escalated', 'archived'] as const;
export type FeedbackStatus = (typeof FEEDBACK_STATUSES)[number];

export function isFeedbackStatus(value: string): value is FeedbackStatus {
  return (FEEDBACK_STATUSES as readonly string[]).includes(value);
}

export const FEEDBACK_STATUS_LABEL: Record<FeedbackStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  escalated: 'Escalated to platform',
  archived: 'Archived',
};

/** Max length for parent message body. */
export const FEEDBACK_BODY_MAX = 2000;
export const FEEDBACK_BODY_MIN = 4;

export const FEEDBACK_ID_RE = /^[0-9a-f-]{36}$/i;

export type FeedbackPerson = {
  id: string;
  full_name: string | null;
  email?: string | null;
};

export type FeedbackReply = {
  id: string;
  feedback_id: string;
  author_id: string;
  body: string;
  created_at: string;
  from_club: boolean;
  author?: FeedbackPerson | null;
};

export type ClubFeedback = {
  id: string;
  club_id: string;
  submitted_by: string;
  category: FeedbackCategory;
  body: string;
  session_id: string | null;
  status: FeedbackStatus;
  admin_note: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
  escalated_at: string | null;
  escalated_by: string | null;
  escalated_note: string | null;
  picked_up_by: string | null;
  picked_up_at: string | null;
  last_reply_at: string | null;
  created_at: string;
  updated_at: string;
  /** Joined for admin list */
  submitter?: FeedbackPerson | null;
  picker?: FeedbackPerson | null;
  assignees?: FeedbackPerson[];
  replies?: FeedbackReply[];
  reply_count?: number;
  can_reply?: boolean;
  can_pickup?: boolean;
  session?: {
    id: string;
    session_date: string;
    start_time: string;
    squad_name: string | null;
  } | null;
};

export const FEEDBACK_REPLY_NOTICE_KIND = 'feedback_reply';
export const FEEDBACK_REPLY_NOTICE_TITLE = 'The club replied to your feedback';

export function isFeedbackId(value: string): boolean {
  return FEEDBACK_ID_RE.test(value.trim());
}

/** Parent PWA list. Thread windows close here (replace), including notice deep-links. */
export const FEEDBACK_PWA_LIST_HREF = '/feedback';
/** Staff inbox list. Thread windows close here (replace). */
export const FEEDBACK_STAFF_LIST_HREF = '/app/feedback';

export const FEEDBACK_SCHEMA_REQUIRED_MESSAGE =
  'This conversation is open. Sending a reply needs a database update that is still applying — try again in a moment.';

export function feedbackThreadHref(feedbackId: string): string {
  return `${FEEDBACK_PWA_LIST_HREF}/${feedbackId.trim()}`;
}

export function feedbackStaffThreadHref(feedbackId: string): string {
  return `${FEEDBACK_STAFF_LIST_HREF}/${feedbackId.trim()}`;
}

export function parseFeedbackThreadHref(href: string | null | undefined): string | null {
  const raw = href?.trim() ?? '';
  const match = raw.match(/^\/feedback\/([0-9a-f-]{36})\/?$/i);
  return match?.[1] ?? null;
}

export function parseFeedbackStaffThreadHref(href: string | null | undefined): string | null {
  const raw = href?.trim() ?? '';
  const match = raw.match(/^\/app\/feedback\/([0-9a-f-]{36})\/?$/i);
  return match?.[1] ?? null;
}

export function feedbackListPreview(item: Pick<ClubFeedback, 'body' | 'replies'>): string {
  const last = item.replies?.[item.replies.length - 1];
  return last?.body ?? item.body;
}

export function isFeedbackSchemaRequiredError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const rec = err as { status?: number; body?: unknown; message?: string; error?: string };
  const body = rec.body;
  if (body && typeof body === 'object' && (body as { error?: string }).error === 'schema_required') {
    return true;
  }
  if (rec.error === 'schema_required') return true;
  return typeof rec.message === 'string' && /schema_required/i.test(rec.message);
}

export function feedbackSendErrorMessage(err: unknown, fallback: string): string {
  if (isFeedbackSchemaRequiredError(err)) return FEEDBACK_SCHEMA_REQUIRED_MESSAGE;
  if (err && typeof err === 'object' && 'message' in err) {
    const message = (err as { message: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

export function validateFeedbackReply(body: string):
  | { ok: true; body: string }
  | { ok: false; message: string } {
  const trimmed = (body ?? '').trim();
  if (trimmed.length < FEEDBACK_BODY_MIN) {
    return { ok: false, message: 'Please write a short message (a few words at least).' };
  }
  if (trimmed.length > FEEDBACK_BODY_MAX) {
    return { ok: false, message: `Message must be under ${FEEDBACK_BODY_MAX} characters.` };
  }
  return { ok: true, body: trimmed };
}

export function canPickUpFeedback(input: {
  userId: string;
  submittedBy: string;
  canViewInbox: boolean;
}): boolean {
  if (!input.canViewInbox) return false;
  if (input.userId === input.submittedBy) return false;
  return true;
}

/** Staff reply: picker, or an escalation assignee (including platform owners on escalated items). */
export function canStaffReplyToFeedback(input: {
  userId: string;
  pickedUpBy: string | null;
  assigneeIds: readonly string[];
  status: FeedbackStatus;
  isPlatformOwner: boolean;
}): boolean {
  if (input.pickedUpBy === input.userId) return true;
  if (input.assigneeIds.includes(input.userId)) return true;
  if (input.status === 'escalated' && input.isPlatformOwner) return true;
  return false;
}

export function canParentReplyToFeedback(input: { userId: string; submittedBy: string }): boolean {
  return input.userId === input.submittedBy;
}

export function staffReplyBlockedReason(input: {
  userId: string;
  pickedUpBy: string | null;
  assigneeIds: readonly string[];
  status: FeedbackStatus;
  isPlatformOwner: boolean;
}): string | null {
  if (canStaffReplyToFeedback(input)) return null;
  if (input.status === 'escalated') {
    return 'Only the person who picked this up, or an escalation assignee, can reply.';
  }
  return 'Pick up this item first to reply to the member.';
}

export function feedbackReplyNotice(input: { feedbackId: string; preview: string }): {
  kind: typeof FEEDBACK_REPLY_NOTICE_KIND;
  title: string;
  body: string;
  href: string;
  entity_type: 'club_feedback';
  entity_id: string;
} {
  const preview = input.preview.trim().replace(/\s+/g, ' ');
  return {
    kind: FEEDBACK_REPLY_NOTICE_KIND,
    title: FEEDBACK_REPLY_NOTICE_TITLE,
    body: preview.length > 180 ? `${preview.slice(0, 177).trimEnd()}…` : preview,
    href: feedbackThreadHref(input.feedbackId),
    entity_type: 'club_feedback',
    entity_id: input.feedbackId,
  };
}

/** Parent replies reopen reviewed/archived so the inbox sees the new message. */
export function statusAfterParentReply(status: FeedbackStatus): FeedbackStatus {
  if (status === 'reviewed' || status === 'archived') return 'new';
  return status;
}

export type FeedbackStats = {
  total: number;
  by_status: Record<FeedbackStatus, number>;
  by_category: Record<FeedbackCategory, number>;
  last_7_days: number;
  last_30_days: number;
  newest_at: string | null;
};

export function validateFeedbackSubmit(input: {
  category: string;
  body: string;
  session_id?: string | null;
}):
  | {
      ok: true;
      category: FeedbackCategory;
      body: string;
      session_id: string | null;
    }
  | { ok: false; message: string } {
  if (!isFeedbackCategory(input.category)) {
    return {
      ok: false,
      message: `category must be one of: ${FEEDBACK_CATEGORY_CODES.join(', ')}`,
    };
  }
  const body = (input.body ?? '').trim();
  if (body.length < FEEDBACK_BODY_MIN) {
    return { ok: false, message: 'Please write a short message (a few words at least).' };
  }
  if (body.length > FEEDBACK_BODY_MAX) {
    return { ok: false, message: `Message must be under ${FEEDBACK_BODY_MAX} characters.` };
  }
  const sessionId = input.session_id?.trim() || null;
  if (sessionId && !/^[0-9a-f-]{36}$/i.test(sessionId)) {
    return { ok: false, message: 'Invalid session_id.' };
  }
  return { ok: true, category: input.category, body, session_id: sessionId };
}

export function emptyFeedbackStats(): FeedbackStats {
  return {
    total: 0,
    by_status: { new: 0, reviewed: 0, escalated: 0, archived: 0 },
    by_category: {
      app_help: 0,
      swimming_sessions: 0,
      club_suggestions: 0,
      general_queries: 0,
    },
    last_7_days: 0,
    last_30_days: 0,
    newest_at: null,
  };
}
